import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Download, ArrowLeft, Star, Clock, Calendar, Loader2, Film } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import Navbar from "@/components/Navbar";
import FavoriteButton from "@/components/FavoriteButton";
import { Button } from "@/components/ui/button";
import { getVodInfo, imageUrl, proxyStreamUrl } from "@/lib/xtream";
import { toast } from "sonner";

export default function MovieDetail() {
  const { id } = useParams();
  const { creds } = useAuth();
  const { openPlayer } = usePlayer();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getVodInfo(creds, id)
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(e?.response?.data?.detail || "Could not load this title."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id, creds]);

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-40 text-slate-500">
          <Loader2 className="h-8 w-8 animate-spin text-[#14A3FF]" />
        </div>
      </Shell>
    );
  }
  if (error || !data?.movie_data) {
    return (
      <Shell>
        <div className="text-center py-40 text-slate-400">{error || "Title not found."}</div>
      </Shell>
    );
  }

  const info = data.info || {};
  const md = data.movie_data || {};
  const ext = md.container_extension || info.container_extension || "mp4";
  const streamId = md.stream_id || id;
  const url = proxyStreamUrl(creds, "movie", streamId, ext);
  const downloadUrl = proxyStreamUrl(creds, "movie", streamId, ext, true);
  const backdrop = (Array.isArray(info.backdrop_path) && info.backdrop_path[0]) || info.movie_image;
  const poster = info.movie_image || md.stream_icon;

  const download = () => {
    toast.success("Opening download…");
    window.open(downloadUrl, "_blank");
  };

  const play = () => {
    openPlayer({
      url,
      title: md.name,
      poster: poster ? imageUrl(poster) : null,
      meta: {
        kind: "movie",
        playableId: String(streamId),
        movieId: String(id),
        ext,
        name: md.name,
        poster: poster || null,
      },
    });
  };

  return (
    <Shell>
      {/* Backdrop */}
      <div className="relative h-[48vh] min-h-[340px] w-full">
        {backdrop ? (
          <img src={imageUrl(backdrop)} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-[#0C1017]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#05070A] via-[#05070A]/70 to-[#05070A]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#05070A]/90 to-transparent" />
        <div className="relative mx-auto max-w-[1600px] px-4 sm:px-8 lg:px-12 pt-6">
          <Button
            data-testid="back-button"
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-slate-200 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
          </Button>
        </div>
      </div>

      <div className="relative mx-auto max-w-[1600px] px-4 sm:px-8 lg:px-12 -mt-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-3"
          >
            <div className="aspect-[2/3] rounded-2xl overflow-hidden border border-white/10 bg-[#131924] shadow-2xl">
              {poster ? (
                <img src={imageUrl(poster)} alt={md.name} className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-slate-700">
                  <Film className="h-12 w-12" />
                </div>
              )}
            </div>
          </motion.div>

          <div className="lg:col-span-9 pt-2">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white font-heading mb-3">
              {md.name}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-300 mb-5">
              {info.rating ? (
                <span className="flex items-center gap-1.5 text-[#E5A93C] font-semibold">
                  <Star className="h-4 w-4 fill-[#E5A93C]" /> {parseFloat(info.rating).toFixed(1)}
                </span>
              ) : null}
              {info.releasedate ? (
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" /> {String(info.releasedate).slice(0, 4)}
                </span>
              ) : null}
              {info.duration ? (
                <span className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4" /> {info.duration}
                </span>
              ) : null}
              {info.genre ? (
                <span className="px-2.5 py-0.5 rounded-full bg-white/10 text-xs">{info.genre}</span>
              ) : null}
            </div>

            <div className="flex flex-wrap gap-3 mb-7">
              <Button
                data-testid="movie-play-button"
                onClick={play}
                className="h-12 px-7 bg-[#14A3FF] hover:bg-[#0E8FE6] text-white font-semibold text-base active:scale-95 transition-transform"
              >
                <Play className="h-5 w-5 mr-2 fill-white" /> Play
              </Button>
              <Button
                data-testid="movie-download-button"
                onClick={download}
                variant="outline"
                className="h-12 px-7 border-white/15 bg-white/5 text-white hover:bg-white/10 font-semibold text-base active:scale-95 transition-transform"
              >
                <Download className="h-5 w-5 mr-2" /> Download
              </Button>
              <FavoriteButton creds={creds} kind="movie" item={{ ...md, stream_id: streamId, stream_icon: poster, rating: info.rating, year: info.releasedate ? String(info.releasedate).slice(0, 4) : undefined }} variant="button" />
            </div>

            {info.plot ? (
              <p className="text-slate-300 leading-relaxed max-w-3xl mb-6">{info.plot}</p>
            ) : null}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-4 gap-x-6 max-w-3xl">
              <Meta label="Cast" value={info.cast} />
              <Meta label="Director" value={info.director} />
              <Meta label="Genre" value={info.genre} />
              <Meta label="Format" value={ext ? ext.toUpperCase() : null} />
            </div>
          </div>
        </div>
      </div>
      <div className="h-16" />
    </Shell>
  );
}

const Meta = ({ label, value }) =>
  value ? (
    <div>
      <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-1">{label}</p>
      <p className="text-sm text-slate-200 line-clamp-2">{value}</p>
    </div>
  ) : null;

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#05070A]">
    <Navbar />
    {children}
  </div>
);
